/**
 * PlaylistManager — Track collections with smart sorting
 */

export class PlaylistManager {
  constructor(deckId) {
    this.deckId = deckId
    this.tracks = [] // Array of { file, name, artist, bpm, key, energy, analysis }
    this.currentIndex = -1
  }

  /**
   * Add track to playlist
   */
  addTrack(trackData) {
    this.tracks.push({
      id: Math.random().toString(36).substr(2, 9),
      ...trackData,
      addedAt: Date.now()
    })
    return this.tracks.length - 1
  }

  /**
   * Remove track by index
   */
  removeTrack(index) {
    if (index < 0 || index >= this.tracks.length) return false
    this.tracks.splice(index, 1)
    if (this.currentIndex >= this.tracks.length) {
      this.currentIndex = this.tracks.length - 1
    }
    return true
  }

  /**
   * Reorder tracks (drag-drop)
   */
  moveTrack(fromIndex, toIndex) {
    if (fromIndex < 0 || toIndex < 0 || fromIndex >= this.tracks.length || toIndex >= this.tracks.length) return false

    const [track] = this.tracks.splice(fromIndex, 1)
    this.tracks.splice(toIndex, 0, track)

    // Update current index if needed
    if (this.currentIndex === fromIndex) {
      this.currentIndex = toIndex
    }
    return true
  }

  /**
   * Sort by BPM (ascending)
   */
  sortByBPM(descending = false) {
    const sorted = [...this.tracks].sort((a, b) => {
      const diff = (a.bpm || 0) - (b.bpm || 0)
      return descending ? -diff : diff
    })
    this.tracks = sorted
    this.currentIndex = -1
  }

  /**
   * Sort by Key using Camelot wheel order
   */
  sortByKey() {
    const camelotOrder = {
      'C': 0, 'G': 1, 'D': 2, 'A': 3, 'E': 4, 'B': 5,
      'F#': 6, 'C#': 7, 'G#': 8, 'D#': 9, 'A#': 10, 'F': 11
    }

    const sorted = [...this.tracks].sort((a, b) => {
      const aOrder = camelotOrder[a.key] ?? 99
      const bOrder = camelotOrder[b.key] ?? 99
      return aOrder - bOrder
    })
    this.tracks = sorted
    this.currentIndex = -1
  }

  /**
   * Sort by Energy (low to high for buildup)
   */
  sortByEnergy(descending = false) {
    const sorted = [...this.tracks].sort((a, b) => {
      const diff = (a.energy || 0) - (b.energy || 0)
      return descending ? -diff : diff
    })
    this.tracks = sorted
    this.currentIndex = -1
  }

  /**
   * Sort by BPM similarity to first track (DJ-optimal flow)
   */
  sortDJOptimal() {
    if (this.tracks.length === 0) return

    const sorted = [this.tracks[0]]
    const remaining = [...this.tracks.slice(1)]

    while (remaining.length > 0) {
      const lastTrack = sorted[sorted.length - 1]
      let bestIdx = 0
      let bestScore = Infinity

      remaining.forEach((track, idx) => {
        // Score: BPM difference + key compatibility
        const bpmDiff = Math.abs((track.bpm || 120) - (lastTrack.bpm || 120))
        const keyMatch = (track.key === lastTrack.key) ? 0 : 5
        const score = bpmDiff + keyMatch

        if (score < bestScore) {
          bestScore = score
          bestIdx = idx
        }
      })

      sorted.push(remaining[bestIdx])
      remaining.splice(bestIdx, 1)
    }

    this.tracks = sorted
    this.currentIndex = -1
  }

  /**
   * Get current track
   */
  getCurrent() {
    return this.currentIndex >= 0 ? this.tracks[this.currentIndex] : null
  }

  /**
   * Advance to next track
   */
  nextTrack() {
    if (this.tracks.length === 0) return null
    this.currentIndex = (this.currentIndex + 1) % this.tracks.length
    return this.getCurrent()
  }

  /**
   * Load track by index
   */
  loadTrack(index) {
    if (index < 0 || index >= this.tracks.length) return null
    this.currentIndex = index
    return this.getCurrent()
  }

  /**
   * Serialize for storage
   */
  serialize() {
    return {
      deckId: this.deckId,
      tracks: this.tracks.map(t => ({
        name: t.name,
        artist: t.artist,
        bpm: t.bpm,
        key: t.key,
        energy: t.energy
      })),
      currentIndex: this.currentIndex
    }
  }

  /**
   * Get playlist as array
   */
  getPlaylist() {
    return this.tracks
  }

  /**
   * Clear playlist
   */
  clear() {
    this.tracks = []
    this.currentIndex = -1
  }
}
